import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../../components/Layout.js";
import { apiFetch } from "../../api.js";

const GRID_API = "/api/accountmastergrid";

const AccountMasterGrid = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = async () => {
    try {
      const res = await apiFetch(GRID_API);
      const data = await res.json();
      setRows(data);
      setFilteredRows(data);
    } catch (err) {
      console.error("Error fetching grid:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = () => {
    const term = searchTerm.toLowerCase();
    const filtered = rows.filter(
      (row) =>
        row.name?.toLowerCase().includes(term) ||
        row.city?.toLowerCase().includes(term) ||
        row.state?.toLowerCase().includes(term) ||
        row.country?.toLowerCase().includes(term),
    );
    setFilteredRows(filtered);
    setCurrentPage(1);
  };

  async function handleEdit(id) {
    try {
      const res = await apiFetch(
        `/api/getRecord/${id}`,
      );
      if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
      const fullData = await res.json();
      navigate("/accounts", { state: { editData: fullData } });
    } catch (err) {
      console.error("Failed to fetch record:", err);
      alert("Unable to fetch record. Check backend.");
    }
  }

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        const res = await apiFetch(
          `/api/accountmaster/${id}`,
          {
            method: "DELETE",
          },
        );
        const data = await res.json();
        if (data.success) {
          const updatedRows = rows.filter((row) => row.unqid !== id);
          setRows(updatedRows);
          setFilteredRows(updatedRows);
          alert(data.message);
        } else {
          alert(data.message);
        }
      } catch (err) {
        console.error("Error deleting record:", err);
        alert("Server error. Check backend logs.");
      }
    }
  };

  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + pageSize);

  /*------------------- Styles--------------*/
  const pageContainerStyle = {
    width: "100%",
    minHeight: "100vh",
    backgroundColor: "#f4f6f8",
    display: "flex",
    justifyContent: "center",
    alignItems: "flex-start",
    padding: "10px",
    boxSizing: "border-box",
  };
  const containerStyle = {
    width: "100%",
    maxWidth: "1200px",
    padding: "15px",
    borderRadius: "8px",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
    backgroundColor: "#fff",
    boxSizing: "border-box",
  };
  const headerStyle = {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "15px",
    gap: "10px",
  };
  const leftHeaderStyle = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    minWidth: "250px",
  };
  const titleStyle = { fontSize: "1.2rem", fontWeight: "bold", flexShrink: 0 };
  const addButtonStyle = {
    padding: "8px 16px",
    backgroundColor: "#3a28a0ff",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  const searchInputStyle = {
    flex: "1",
    minWidth: "140px",
    padding: "8px",
    borderRadius: "4px",
    border: "1px solid #ccc",
  };
  const searchButtonStyle = {
    padding: "8px 12px",
    backgroundColor: "#2196F3",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  };
  const selectStyle = {
    padding: "8px 10px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    backgroundColor: "#fff",
    whiteSpace: "nowrap",
  };
  const tableWrapperStyle = { overflowX: "auto" };
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "600px",
  };
  const thStyle = {
    padding: "10px",
    textAlign: "left",
    backgroundColor: "#8B1E3F",
    color: "#fff",
    border: "1px solid #ddd",
    whiteSpace: "nowrap",
    fontSize: "0.9rem",
  };
  const tdStyle = {
    padding: "8px",
    border: "1px solid #ddd",
    wordWrap: "break-word",
    fontSize: "0.85rem",
  };
  const paginationStyle = {
    marginTop: "12px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    gap: "10px",
    fontSize: "0.9rem",
  };
  const pageBtnStyle = {
    padding: "6px 12px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    cursor: "pointer",
    background: "#f9f9f9",
    fontSize: "0.85rem",
  };

  return (
    <Layout>
      <div style={pageContainerStyle}>
        <div style={containerStyle}>
          <div style={headerStyle}>
            <div style={leftHeaderStyle}>
              <div style={titleStyle}>Account Master Grid</div>
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyle}
              />
              <button style={searchButtonStyle} onClick={handleSearch}>
                Search
              </button>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                style={selectStyle}
              >
                {[10, 20, 50, 100].map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
            </div>
            <button
              style={addButtonStyle}
              onClick={() => navigate("/accounts")}
            >
              Add New
            </button>
          </div>

          <div style={tableWrapperStyle}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Name</th>
                  <th style={thStyle}>Schedule</th>
                  <th style={thStyle}>City</th>
                  <th style={thStyle}>Action</th> {/* Updated header */}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((row) => (
                    <tr key={row.unqid}>
                      <td style={tdStyle}>{row.name}</td>
                      <td style={tdStyle}>{row.schedule}</td>
                      <td style={tdStyle}>{row.city}</td>
                      <td style={tdStyle}>
                        <button
                          style={{
                            ...pageBtnStyle,
                            backgroundColor: "#4CAF50",
                            color: "#fff",
                            marginRight: "5px",
                          }}
                          onClick={() => handleEdit(row.unqid)}
                        >
                          Edit
                        </button>
                        <button
                          style={{
                            ...pageBtnStyle,
                            backgroundColor: "#f44336",
                            color: "#fff",
                          }}
                          onClick={() => handleDelete(row.unqid)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td style={tdStyle} colSpan={4}>
                      No records found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={paginationStyle}>
            <button
              style={pageBtnStyle}
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>
            <span>
              Page {currentPage} of {totalPages || 1}
            </span>
            <button
              style={pageBtnStyle}
              disabled={currentPage === totalPages || totalPages === 0}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default AccountMasterGrid;
